import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
    this.#applyCustomProductSorting();
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };

  async #applyCustomProductSorting() {
    const nativeSorts = new Set([
      'manual',
      'most-relevant',
      'best-selling',
      'title-ascending',
      'title-descending',
      'price-ascending',
      'price-descending',
      'created-ascending',
      'created-descending',
    ]);
    const url = new URL(window.location.href);
    const sortBy = url.searchParams.get('sort_by') || '';

    if (!sortBy.includes(',') || nativeSorts.has(sortBy)) return;

    const sectionId = this.getAttribute('section-id');
    const { grid } = this.refs;
    const cache = sectionId ? this.querySelector(`#CustomSortCards-${sectionId}`) : null;

    if (!sectionId || !grid) return;

    if (!cache || !grid.querySelector('[data-product-handle]')) {
      await this.#refreshCustomSortSection(sectionId);
      return;
    }

    this.#pinCustomSortedCards(grid, cache, url, sortBy);
  }

  async #refreshCustomSortSection(sectionId) {
    if (this.dataset.customSortRefreshAttempted === 'true') return;

    this.dataset.customSortRefreshAttempted = 'true';

    const sectionUrl = new URL(window.location.href);
    sectionUrl.searchParams.set('section_id', sectionId);

    try {
      const response = await fetch(sectionUrl.toString(), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });

      if (!response.ok) return;

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const freshSection = doc.querySelector(`#shopify-section-${sectionId}`);
      const currentSection = this.closest('.shopify-section');

      if (freshSection && currentSection) currentSection.replaceWith(freshSection);
    } catch (error) {
      console.error('Custom product sorting failed to refresh the collection section.', error);
    }
  }

  #pinCustomSortedCards(grid, cache, url, sortBy) {
    if (grid.dataset.customSortApplied === 'true') return;

    const handles = sortBy
      .split(',')
      .map((handle) => handle.trim())
      .filter(Boolean);
    const customHandles = new Set(handles);

    if (!customHandles.size) return;

    const removeDuplicateCards = () => {
      for (const card of grid.querySelectorAll('[data-product-handle]')) {
        if (card.dataset.customSortPinned === 'true') continue;
        if (customHandles.has(card.dataset.productHandle)) card.remove();
      }
    };

    if (Number(url.searchParams.get('page') || '1') <= 1) {
      const fragment = document.createDocumentFragment();
      let pinnedCount = 0;
      const templates = Array.from(cache.querySelectorAll('template[data-custom-sort-handle]'));

      removeDuplicateCards();

      for (const handle of handles) {
        const template = templates.find((item) => item.dataset.customSortHandle === handle);
        const card = template?.content.firstElementChild?.cloneNode(true);

        if (!card) continue;

        card.dataset.customSortPinned = 'true';
        fragment.appendChild(card);
        pinnedCount += 1;
      }

      if (pinnedCount) grid.prepend(fragment);
    }

    removeDuplicateCards();
    new MutationObserver(removeDuplicateCards).observe(grid, { childList: true });
    grid.dataset.customSortApplied = 'true';
  }
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
